// Adding and editing lessons in the course workspace (CourseLessons): the lesson text, draft or
// published, free preview, "save and add another", adding many lessons from a list, creating a
// module without leaving the form, and the module groups of the lesson list.
const lessonAuthoring = {
  en: {
    content: {
      label: 'Lesson text',
      placeholder: 'What students read: the explanation, rules, examples…',
      hint: 'A blank line starts a new paragraph. Lines starting with "- " make a list, "## " a heading, **bold**. Web addresses become links.',
      toolbar: 'Text formatting',
      bold: 'Bold',
      boldSample: 'important',
      list: 'List',
      heading: 'Heading',
      preview: 'Preview',
      write: 'Write',
      previewTitle: 'What students see',
      previewEmpty: 'No text yet',
      htmlMode: 'This text has its own formatting (a table, a picture or a link with its own text), so you edit it as HTML. Check it in Preview.',
    },
    visibility: {
      label: 'Status',
      draft: 'Draft',
      published: 'Published',
      draftHint: 'Students do not see a draft. Publish it when it is ready.',
      publishedHint: 'Students enrolled in the course see this lesson.',
    },
    sample: {
      label: 'Free preview',
      hint: 'People who have not bought the course can open this lesson too.',
    },
    saveAndAdd: 'Save and add another',
    added: '"{title}" added. Type the next one.',
    bulk: {
      open: 'Add from a list',
      title: 'Add several lessons',
      label: 'Lesson titles',
      placeholder: 'One lesson title per line:\nThe alphabet\nGreetings\nNumbers',
      hint: 'Each line is one lesson, added in this order. A leading "1." or "-" is dropped.',
      summary: 'Lessons to add: {count}',
      tooShort: 'Line {line}: a title needs at least 3 characters',
      tooLong: 'Line {line}: a title can have at most 100 characters',
      submit: 'Add lessons ({count})',
      progress: 'Adding {done} of {total}…',
      failed: '{done} added; "{title}" could not be added. Try the rest again.',
      retryRest: 'Add the rest',
      done: 'Lessons added: {count}',
      cancel: 'Cancel',
    },
    module: {
      newOption: '+ New module',
      newLabel: 'New module name',
      newPlaceholder: 'e.g. Module 1: The alphabet',
      create: 'Add',
      cancel: 'Cancel',
      tooShort: 'A module name needs at least 3 characters',
      createFailed: 'Could not create the module. Try again.',
      loadFailed: 'Could not load the modules.',
      retry: 'Try again',
      noModuleGroup: 'Lessons without a module',
      groupCount: 'Lessons: {count}',
      addToGroup: 'Add lesson',
      addToGroupAria: 'Add a lesson to "{module}"',
    },
    row: {
      draft: 'Draft',
      sample: 'Free',
      publish: 'Publish',
      unpublish: 'Back to draft',
      statusFailed: 'Could not change the lesson status.',
      publishAll: 'Publish all ({count})',
      publishAllTitle: 'Publish {count} draft lessons?',
      publishAllHint: 'Students enrolled in the course will see them.',
      noVideo: 'No video',
      addVideo: 'Add video',
      addVideoAria: 'Add a video to "{title}"',
    },
  },
  ru: {
    content: {
      label: 'Текст урока',
      placeholder: 'Что читают ученики: объяснение, правила, примеры…',
      hint: 'Пустая строка начинает новый абзац. Строки с «- » — список, «## » — заголовок, **жирный**. Веб-адреса становятся ссылками.',
      toolbar: 'Оформление текста',
      bold: 'Жирный',
      boldSample: 'важно',
      list: 'Список',
      heading: 'Заголовок',
      preview: 'Просмотр',
      write: 'Текст',
      previewTitle: 'Так увидят ученики',
      previewEmpty: 'Текста пока нет',
      htmlMode: 'У этого текста особое оформление (таблица, картинка или ссылка со своим текстом), поэтому он редактируется как HTML. Проверьте его в просмотре.',
    },
    visibility: {
      label: 'Статус',
      draft: 'Черновик',
      published: 'Опубликован',
      draftHint: 'Черновик ученики не видят. Опубликуйте, когда он будет готов.',
      publishedHint: 'Урок видят ученики, записанные на курс.',
    },
    sample: {
      label: 'Бесплатный просмотр',
      hint: 'Этот урок смогут открыть и те, кто не купил курс.',
    },
    saveAndAdd: 'Сохранить и добавить ещё',
    added: '«{title}» добавлен. Введите следующий.',
    bulk: {
      open: 'Добавить списком',
      title: 'Добавить несколько уроков',
      label: 'Названия уроков',
      placeholder: 'По одному названию в строке:\nАлфавит\nПриветствия\nЧисла',
      hint: 'Каждая строка — один урок, они добавятся в этом порядке. «1.» или «-» в начале убираются.',
      summary: 'Будет добавлено уроков: {count}',
      tooShort: 'Строка {line}: в названии нужно хотя бы 3 символа',
      tooLong: 'Строка {line}: в названии не больше 100 символов',
      submit: 'Добавить уроки ({count})',
      progress: 'Добавляется {done} из {total}…',
      failed: 'Добавлено: {done}; «{title}» добавить не удалось. Попробуйте добавить остальные ещё раз.',
      retryRest: 'Добавить остальные',
      done: 'Добавлено уроков: {count}',
      cancel: 'Отмена',
    },
    module: {
      newOption: '+ Новый модуль',
      newLabel: 'Название нового модуля',
      newPlaceholder: 'например, Модуль 1: Алфавит',
      create: 'Добавить',
      cancel: 'Отмена',
      tooShort: 'В названии модуля нужно хотя бы 3 символа',
      createFailed: 'Не удалось создать модуль. Попробуйте ещё раз.',
      loadFailed: 'Не удалось загрузить модули.',
      retry: 'Повторить',
      noModuleGroup: 'Уроки без модуля',
      groupCount: 'Уроков: {count}',
      addToGroup: 'Добавить урок',
      addToGroupAria: 'Добавить урок в «{module}»',
    },
    row: {
      draft: 'Черновик',
      sample: 'Бесплатно',
      publish: 'Опубликовать',
      unpublish: 'Вернуть в черновик',
      statusFailed: 'Не удалось изменить статус урока.',
      publishAll: 'Опубликовать все ({count})',
      publishAllTitle: 'Опубликовать черновики ({count})?',
      publishAllHint: 'Их увидят ученики, записанные на курс.',
      noVideo: 'Нет видео',
      addVideo: 'Добавить видео',
      addVideoAria: 'Добавить видео к «{title}»',
    },
  },
  uz: {
    content: {
      label: 'Dars matni',
      placeholder: "O'quvchilar o'qiydigan matn: tushuntirish, qoidalar, misollar…",
      hint: "Bo'sh qator — yangi xatboshi. «- » bilan boshlangan qatorlar — ro'yxat, «## » — sarlavha, **qalin**. Havolalar o'zi bosiladigan bo'ladi.",
      toolbar: 'Matn bezagi',
      bold: 'Qalin',
      boldSample: 'muhim',
      list: "Ro'yxat",
      heading: 'Sarlavha',
      preview: "Ko'rinishi",
      write: 'Matn',
      previewTitle: "O'quvchilar shunday ko'radi",
      previewEmpty: "Hozircha matn yo'q",
      htmlMode: "Bu matnning o'z bezagi bor (jadval, rasm yoki o'z matni bor havola), shuning uchun u HTML sifatida tahrirlanadi. «Ko'rinishi»da tekshirib oling.",
    },
    visibility: {
      label: 'Holati',
      draft: 'Qoralama',
      published: 'Nashr qilingan',
      draftHint: "Qoralama darsni o'quvchilar ko'rmaydi. Tayyor bo'lgach nashr qiling.",
      publishedHint: "Dars kursga yozilgan o'quvchilarga ko'rinadi.",
    },
    sample: {
      label: 'Bepul namuna',
      hint: "Kursni sotib olmaganlar ham bu darsni ocha oladi.",
    },
    saveAndAdd: "Saqlash va yana qo'shish",
    added: "«{title}» qo'shildi. Keyingisini yozing.",
    bulk: {
      open: "Ro'yxatdan qo'shish",
      title: "Bir nechta dars qo'shish",
      label: 'Dars nomlari',
      placeholder: 'Har qatorda bitta dars nomi:\nAlifbo\nSalomlashish\nRaqamlar',
      hint: "Har qator — bitta dars, shu tartibda qo'shiladi. Boshidagi «1.» yoki «-» olib tashlanadi.",
      summary: "{count} ta dars qo'shiladi",
      tooShort: "{line}-qator: nom kamida 3 belgidan iborat bo'lsin",
      tooLong: '{line}-qator: nom 100 belgidan oshmasin',
      submit: "{count} ta darsni qo'shish",
      progress: "Qo'shilmoqda: {done} / {total}…",
      failed: "{done} ta dars qo'shildi, «{title}» qo'shilmadi. Qolganlarini qayta qo'shib ko'ring.",
      retryRest: "Qolganlarini qo'shish",
      done: "{count} ta dars qo'shildi",
      cancel: 'Bekor qilish',
    },
    module: {
      newOption: '+ Yangi modul',
      newLabel: 'Yangi modul nomi',
      newPlaceholder: 'masalan, 1-modul: Alifbo',
      create: "Qo'shish",
      cancel: 'Bekor qilish',
      tooShort: "Modul nomi kamida 3 belgidan iborat bo'lsin",
      createFailed: "Modulni yaratib bo'lmadi. Qayta urinib ko'ring.",
      loadFailed: "Modullarni yuklab bo'lmadi.",
      retry: 'Qayta urinish',
      noModuleGroup: 'Modulsiz darslar',
      groupCount: '{count} ta dars',
      addToGroup: "Dars qo'shish",
      addToGroupAria: "«{module}»ga dars qo'shish",
    },
    row: {
      draft: 'Qoralama',
      sample: 'Bepul',
      publish: 'Nashr qilish',
      unpublish: 'Qoralamaga qaytarish',
      statusFailed: "Dars holatini o'zgartirib bo'lmadi.",
      publishAll: 'Hammasini nashr qilish ({count})',
      publishAllTitle: '{count} ta qoralama darsni nashr qilasizmi?',
      publishAllHint: "Ularni kursga yozilgan o'quvchilar ko'radi.",
      noVideo: "Video yo'q",
      addVideo: "Video qo'shish",
      addVideoAria: "«{title}» darsiga video qo'shish",
    },
  },
};

export default lessonAuthoring;
